import type {
  AuditLogRow,
  AuditSummary,
  Lookups,
  RefCollector,
  Summarizer,
} from "./index";
import {
  arr,
  asRecord,
  bool,
  buildSummary,
  countText,
  entityLabelOf,
  fieldsFragment,
  formatCents,
  formatIsoDay,
  isUuid,
  lookupFragment,
  missingText,
  num,
  plural,
  refFragment,
  resolvePerson,
  resolveTarget,
  str,
  translate,
  type Fragment,
  type Resolution,
} from "./vocab";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

const CARD_ROLE_LABELS: Record<string, string> = {
  primary: "principal",
  reserve: "reserva",
  other: "adicional",
};

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  elo: "Elo",
  hipercard: "Hipercard",
  diners: "Diners",
  discover: "Discover",
  jcb: "JCB",
  unionpay: "UnionPay",
};

/** Only these four are audited; reuse/reopen stay as harmless entries. */
const PREVIOUS_ATTEMPT_OUTCOMES: Record<string, string> = {
  replace: "substituída por uma nova tentativa",
  cleanup_canceled: "tentativa cancelada",
  takeover: "reserva anterior assumida",
  foreign: "de outra compra, não mexida",
  reuse: "tentativa reaproveitada",
  reopen: "tentativa reaberta",
};

const PAYMENT_INTENT_STATUS: Record<string, string> = {
  requires_payment_method: "aguardando cartão",
  requires_action: "aguardando autenticação",
  requires_confirmation: "aguardando confirmação",
  requires_capture: "aguardando captura",
  processing: "em processamento",
  canceled: "cancelado",
  succeeded: "pago",
};

const ORPHAN_EVENT_TYPES: Record<string, string> = {
  "invoice.paid": "fatura paga",
  "invoice.payment_failed": "fatura não paga",
  "customer.subscription.created": "assinatura criada",
  "customer.subscription.updated": "assinatura alterada",
  "customer.subscription.deleted": "assinatura cancelada",
};

const ORPHAN_REASONS: Record<string, string> = {
  no_klubora_metadata: "sem dados do Klubora",
  profile_gone: "perfil não existe mais",
  invitation_gone: "convite não existe mais",
  writer_already_finished: "já processada por outro caminho",
};

const STRIPE_MODES: Record<string, string> = {
  live: "modo produção",
  test: "modo teste",
};

const CONNECT_REFUSALS: Record<string, string> = {
  missing_key: "chave não informada",
  unrestricted_key: "chave sem restrição",
  invalid_restricted_key: "chave restrita inválida",
  missing_permissions: "faltam permissões na chave",
  publishable_wrong_account: "chave pública de outra conta",
  publishable_wrong_mode: "chave pública de outro modo",
  credential_not_stored: "não foi possível guardar a chave",
  endpoint_creation_failed: "falha ao criar o webhook",
  signing_secret_not_stored: "não foi possível guardar o segredo do webhook",
  connection_state_not_written: "falha ao gravar a conexão",
  locked_by_first_charge: "bloqueada após a primeira cobrança",
};

const TIER_SYNC_STAGES: Record<string, string> = {
  first: "primeira sincronização",
  resync: "ressincronizou",
};

const DELETION_SUBSCRIPTION_OUTCOMES: Record<string, string> = {
  "nothing-to-cancel": "nada a cancelar",
};

/** `details.reason` of a closed invitation (`_shared/close-dead-payment-invitations.ts`). */
const INVITATION_CLOSE_REASONS: Record<string, string> = {
  stripe_subscription_ended: "assinatura encerrada na Stripe",
  stripe_subscription_deleted: "assinatura apagada na Stripe",
  incomplete_attempt_ended_locally: "tentativa encerrada no app",
  sweep_local_subscription_canceled: "assinatura já cancelada (varredura)",
  sweep_stripe_attempt_ended: "tentativa expirada na Stripe (varredura)",
};

/** `details.invitation_type` of a plan-change close; `new` adds nothing to its own label. */
const PLAN_CHANGE_TYPES: Record<string, string> = {
  upgrade: "troca para plano maior",
  downgrade: "troca para plano menor",
};

const RETAINED_TABLES: Record<string, string> = {
  payments: "pagamentos",
  subscriptions: "assinaturas",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function entityPrimary(row: AuditLogRow): string {
  return entityLabelOf(row);
}

/** Date-only values split by string (KTD11); timestamps render as the viewer's local day, like the Quando column. */
function formatDay(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatIsoDay(value);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return formatIsoDay(value);
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** The person named by a details id; an id never recorded falls back to the entity label, not "usuário removido". */
function personPrimary(
  row: AuditLogRow,
  lookups: Lookups,
  id: unknown,
): Resolution | string {
  return isUuid(id) ? resolvePerson(lookups, id) : entityPrimary(row);
}

/** "anfitrião x@y" / "anfitrião removido"; pending, failed and absent ids add nothing. */
function personFragment(
  lookups: Lookups,
  id: unknown,
  prefix: string,
): Fragment {
  if (!isUuid(id)) return null;
  return refFragment(
    resolvePerson(lookups, id),
    (t) => `${prefix} ${t}`,
    () => `${prefix} removido`,
  );
}

function planName(name: string): string {
  return /^plano\b/i.test(name) ? name : `Plano ${name}`;
}

function tierFragment(lookups: Lookups, id: unknown): Fragment {
  if (!isUuid(id)) return null;
  return lookupFragment(resolveTarget(lookups, "tier", id), planName);
}

function tierPrimary(row: AuditLogRow, lookups: Lookups): Resolution | string {
  return isUuid(row.entity_id)
    ? resolveTarget(lookups, "tier", row.entity_id)
    : entityPrimary(row);
}

function reasonFragment(details: unknown): Fragment {
  const reason = str(details, "reason");
  return reason ? `motivo: ${reason}` : null;
}

function amountFragment(type: string | null, value: number | null): Fragment {
  if (value === null) return null;
  if (type === "fixed") return formatCents(value);
  if (type === "percent" || type === null) return `${value}%`;
  return `${value} (${type})`;
}

function monthsFragment(months: number | null): Fragment {
  return months === null ? null : `por ${plural(months, "mês", "meses")}`;
}

function cardRole(details: unknown): string | null {
  const role = str(details, "role");
  return role ? translate(CARD_ROLE_LABELS, role) : null;
}

function cardName(
  details: unknown,
  brandKey: string,
  last4Key: string,
): string | null {
  const brand = str(details, brandKey);
  const last4 = str(details, last4Key);
  if (!brand && !last4) return null;
  const brandLabel = brand
    ? (CARD_BRAND_LABELS[brand.toLowerCase()] ??
      brand.charAt(0).toUpperCase() + brand.slice(1))
    : "Cartão";
  return last4 ? `${brandLabel} final ${last4}` : brandLabel;
}

function countFragment(
  details: unknown,
  key: string,
  one: string,
  many: string,
): Fragment {
  return countText(details, { key, one, many });
}

// ---------------------------------------------------------------------------
// Discount grants (R2, KTD5, KTD6)
// ---------------------------------------------------------------------------

/** The grant terms from `details`, newest shape first. KTD5: a key present but null is a discount. */
function grantTerms(details: unknown): string[] {
  const day = formatIsoDay(str(details, "courtesy_last_day"));
  if (day) return [`Pago fora do app até ${day}`];
  const months = num(details, "duration_months");
  return [
    amountFragment(
      str(details, "discount_type"),
      num(details, "discount_value"),
    ),
    months === null ? "até revogar" : monthsFragment(months),
  ].filter((f): f is string => typeof f === "string");
}

const summarizeGranted: Summarizer = (row, lookups) => {
  const d = row.details;
  return buildSummary(personPrimary(row, lookups, asRecord(d)?.user_id), [
    tierFragment(lookups, asRecord(d)?.tier_id),
    ...grantTerms(d),
    reasonFragment(d),
  ]);
};

const summarizeReplaced: Summarizer = (row, lookups) => {
  const d = row.details;
  const [first, ...rest] = grantTerms(d);
  return buildSummary(personPrimary(row, lookups, asRecord(d)?.user_id), [
    first ? `agora: ${first}` : null,
    ...rest,
    tierFragment(lookups, asRecord(d)?.tier_id),
    reasonFragment(d),
  ]);
};

/**
 * The revoked grant's `courtesy_last_day` ("YYYY-MM-DD"), resolved through the
 * `subscription_discount_grant` kind; null when unknown. Feed it to
 * `labelForAuditRow` as `grantLastDay` so label and summary agree (KTD6).
 */
export function revokedGrantLastDay(
  row: AuditLogRow,
  lookups: Lookups,
): string | null {
  if (row.action !== "membership_discount_revoked" || !isUuid(row.entity_id))
    return null;
  const res = resolveTarget(
    lookups,
    "subscription_discount_grant",
    row.entity_id,
  );
  if (res.state !== "ok") return null;
  return formatIsoDay(res.text) ? res.text.slice(0, 10) : null;
}

const summarizeRevoked: Summarizer = (row, lookups) => {
  const d = row.details;
  const lastDay = formatIsoDay(revokedGrantLastDay(row, lookups));
  // Without the grant's date a null duration is not proof of "até revogar".
  const previous = amountFragment(
    str(d, "previous_type"),
    num(d, "previous_value"),
  );
  const terms: Fragment[] = lastDay
    ? [`era pago fora do app até ${lastDay}`]
    : [
        previous ? `era ${previous}` : null,
        monthsFragment(num(d, "previous_duration_months")),
      ];
  return buildSummary(personPrimary(row, lookups, asRecord(d)?.user_id), [
    ...terms,
    reasonFragment(d),
  ]);
};

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

function requestedBy(row: AuditLogRow): Fragment {
  if (row.actor_type === "staff") return "pedido pela equipe";
  if (row.actor_type === "member") return "pedido pelo membro";
  return null;
}

const summarizeNonRenewalRequested: Summarizer = (row, lookups) => {
  const d = row.details;
  const day = formatDay(asRecord(d)?.scheduled_change_at);
  return buildSummary(personPrimary(row, lookups, asRecord(d)?.owner_user_id), [
    day ? `termina em ${day}` : null,
    requestedBy(row),
  ]);
};

function billingOptionFragment(
  lookups: Lookups,
  id: unknown,
  format: (label: string) => string,
): Fragment {
  if (!isUuid(id)) return null;
  return lookupFragment(
    resolveTarget(lookups, "tier_billing_option", id),
    format,
  );
}

const summarizeNonRenewalCanceled: Summarizer = (row, lookups) => {
  const d = asRecord(row.details);
  return buildSummary(personPrimary(row, lookups, d?.owner_user_id), [
    "a assinatura volta a renovar",
    billingOptionFragment(
      lookups,
      d?.restored_billing_option_id,
      (l) => `na forma ${l}`,
    ),
  ]);
};

const summarizeBillingOptionChange: Summarizer = (row, lookups) => {
  const d = asRecord(row.details);
  const day = formatDay(d?.scheduled_change_at);
  return buildSummary(personPrimary(row, lookups, d?.owner_user_id), [
    billingOptionFragment(
      lookups,
      d?.scheduled_billing_option_id,
      (l) => `passa para ${l}`,
    ),
    day ? `a partir de ${day}` : null,
  ]);
};

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

const summarizeTierStripeSynced: Summarizer = (row, lookups) => {
  const d = row.details;
  const stage = str(d, "stage");
  const cents = num(d, "monthly_price_cents");
  const interval = str(d, "billing_interval");
  const count = num(d, "billing_interval_count") ?? 1;
  let price: Fragment = null;
  if (cents !== null) {
    if (!interval || (interval === "month" && count === 1))
      price = `${formatCents(cents)}/mês`;
    else if (interval === "year" && count === 1)
      price = `${formatCents(cents)}/ano`;
    else if (interval === "month")
      price = `${formatCents(cents)} a cada ${count} meses`;
    else if (interval === "year")
      price = `${formatCents(cents)} a cada ${count} anos`;
    else price = `${formatCents(cents)} (${interval})`;
  }
  return buildSummary(tierPrimary(row, lookups), [
    stage ? translate(TIER_SYNC_STAGES, stage) : null,
    price,
  ]);
};

const summarizeTierBillingOptionDeactivated: Summarizer = (row, lookups) => {
  const d = row.details;
  const primary: Resolution | string = isUuid(row.entity_id)
    ? resolveTarget(lookups, "tier_billing_option", row.entity_id)
    : entityPrimary(row);
  const formPrimary =
    typeof primary !== "string" && primary.state === "ok"
      ? { ...primary, text: `Forma ${primary.text}` }
      : primary;
  return buildSummary(formPrimary, [
    tierFragment(lookups, asRecord(d)?.tier_id),
    countFragment(
      d,
      "live_subscribers",
      "assinante afetado",
      "assinantes afetados",
    ),
    countFragment(d, "notice_dispatched", "avisado", "avisados"),
  ]);
};

// ---------------------------------------------------------------------------
// Closed invitations (KLUB-287)
// ---------------------------------------------------------------------------

function closeReasonFragment(details: unknown): Fragment {
  const reason = str(details, "reason");
  return reason ? translate(INVITATION_CLOSE_REASONS, reason) : null;
}

const summarizeInvitationClosedDeadPayment: Summarizer = (row, lookups) => {
  const d = row.details;
  return buildSummary(personPrimary(row, lookups, asRecord(d)?.user_id), [
    closeReasonFragment(d),
  ]);
};

const summarizeInvitationClosedSubscriptionEnded: Summarizer = (row, lookups) => {
  const d = row.details;
  const type = str(d, "invitation_type");
  return buildSummary(personPrimary(row, lookups, asRecord(d)?.user_id), [
    type ? translate(PLAN_CHANGE_TYPES, type) : null,
    closeReasonFragment(d),
  ]);
};

// ---------------------------------------------------------------------------
// Payments and cards
// ---------------------------------------------------------------------------

const summarizePaymentDeclined: Summarizer = (row, lookups) => {
  const d = asRecord(row.details);
  const cents = num(d, "amount_cents");
  const activity =
    str(d, "entity_type") === "activity" && isUuid(d?.entity_id)
      ? lookupFragment(resolveTarget(lookups, "activity", d?.entity_id))
      : null;
  return buildSummary(personPrimary(row, lookups, d?.user_id), [
    activity,
    cents === null ? null : formatCents(cents),
    str(d, "message"),
    bool(d, "host_pays") === true ? "anfitrião paga" : null,
  ]);
};

const summarizePreviousAttempt: Summarizer = (row, lookups) => {
  const d = row.details;
  const outcome = str(d, "outcome");
  const status = str(d, "status");
  const invites = num(d, "cancelled_invites");
  const primary: Resolution | string = isUuid(row.entity_id)
    ? resolveTarget(lookups, "activity", row.entity_id)
    : entityPrimary(row);
  return buildSummary(primary, [
    personFragment(lookups, asRecord(d)?.host_user_id, "anfitrião"),
    outcome ? translate(PREVIOUS_ATTEMPT_OUTCOMES, outcome) : null,
    // `takeover` rows carry no payment intent, so there is no payment status to show.
    str(d, "payment_intent_id") && status
      ? `pagamento ${translate(PAYMENT_INTENT_STATUS, status)}`
      : null,
    invites !== null && invites > 0
      ? plural(invites, "convite cancelado", "convites cancelados")
      : null,
    bool(d, "lot_cleanup_failed") === true ? "limpeza incompleta" : null,
  ]);
};

function cardSummary(
  primary: string | null,
  row: AuditLogRow,
  secondary: Fragment[],
): AuditSummary {
  return buildSummary(primary ?? entityPrimary(row), secondary);
}

const summarizeCardAttached: Summarizer = (row) =>
  cardSummary(cardName(row.details, "brand", "last4"), row, [
    cardRole(row.details),
  ]);

const summarizeCardSaved: Summarizer = (row) =>
  cardSummary(cardName(row.details, "card_brand", "card_last4"), row, [
    cardRole(row.details),
  ]);

const summarizeCardDetached: Summarizer = (row) => {
  const role = cardRole(row.details);
  return buildSummary(role ? `Cartão ${role}` : entityPrimary(row));
};

const summarizeCardSetDefault: Summarizer = () =>
  buildSummary("Cartão principal", ["trocado por outro cartão"]);

const summarizeCardSetRole: Summarizer = (row) => {
  const role = cardRole(row.details);
  return cardSummary(cardName(row.details, "card_brand", "card_last4"), row, [
    role ? `agora é ${role}` : null,
  ]);
};

function translatedList(
  values: unknown[] | null,
  map: Record<string, string>,
): string | null {
  const labels = (values ?? [])
    .filter((v): v is string => typeof v === "string" && v !== "")
    .map((v) => translate(map, v));
  return labels.length > 0 ? labels.join(", ") : null;
}

const summarizeCardsDetachedOnDeletion: Summarizer = (row) => {
  const d = row.details;
  const subs = str(d, "subscriptions");
  const retained = translatedList(arr(d, "retained_fiscal"), RETAINED_TABLES);
  return buildSummary("Cartões da conta excluída", [
    countFragment(d, "detached_cards", "cartão removido", "cartões removidos"),
    subs
      ? `assinaturas: ${translate(DELETION_SUBSCRIPTION_OUTCOMES, subs)}`
      : null,
    retained ? `mantidos: ${retained}` : null,
  ]);
};

/** `identity_snapshot` and `basis` are never shown: the first is personal data of a deleted account. */
const summarizePaymentsRetained: Summarizer = (row) => {
  const d = row.details;
  const day = formatDay(asRecord(d)?.review_date);
  const tables = translatedList(arr(d, "tables"), RETAINED_TABLES);
  return buildSummary(missingText("account"), [
    day ? `retidos por lei até ${day}` : null,
    tables,
  ]);
};

const summarizeInvoiceRetry =
  (outcome: "ok" | "failed"): Summarizer =>
  (row) => {
    if (outcome === "ok")
      return buildSummary(entityPrimary(row), ["paga na nova tentativa"]);
    const error = str(row.details, "error");
    return buildSummary(entityPrimary(row), [
      error ? `recusada: ${error}` : "recusada na nova tentativa",
    ]);
  };

// ---------------------------------------------------------------------------
// Stripe connection and webhooks
// ---------------------------------------------------------------------------

const summarizeOrphanSubscription: Summarizer = (row, lookups) => {
  const d = row.details;
  const eventType = str(d, "event_type");
  const reason = str(d, "reason");
  const primary =
    row.entity_type === "profile"
      ? personPrimary(row, lookups, row.entity_id)
      : entityPrimary(row);
  return buildSummary(primary, [
    eventType ? translate(ORPHAN_EVENT_TYPES, eventType) : null,
    reason ? translate(ORPHAN_REASONS, reason) : null,
    tierFragment(lookups, asRecord(d)?.metadata_tier_id),
  ]);
};

const summarizeStripeConnected: Summarizer = (row) => {
  const d = row.details;
  const mode = str(d, "mode");
  const failed = arr(d, "failed_compensations");
  return buildSummary(entityPrimary(row), [
    mode ? translate(STRIPE_MODES, mode) : null,
    bool(d, "replaced") === true ? "substituiu a conexão anterior" : null,
    failed && failed.length > 0 ? "limpeza incompleta" : null,
  ]);
};

const summarizeStripeRefused: Summarizer = (row) => {
  const reason = str(row.details, "reason");
  return buildSummary(entityPrimary(row), [
    reason ? `motivo: ${translate(CONNECT_REFUSALS, reason)}` : null,
  ]);
};

// ---------------------------------------------------------------------------
// Registries
// ---------------------------------------------------------------------------

/** Primary = entity label, secondary = `{verb} {fields}`; for rows whose target lives in a dropped table (KTD3). */
const fieldsOnly =
  (verb: string): Summarizer =>
  (row) =>
    buildSummary(entityPrimary(row), [fieldsFragment(row.details, verb)]);

const tierWith =
  (secondary: (details: unknown) => Fragment[]): Summarizer =>
  (row, lookups) =>
    buildSummary(tierPrimary(row, lookups), secondary(row.details));

/** Summaries for the billing domain, keyed by audit_log.action. */
export const summarizers: Partial<Record<string, Summarizer>> = {
  membership_discount_granted: summarizeGranted,
  membership_discount_replaced: summarizeReplaced,
  membership_discount_revoked: summarizeRevoked,
  subscription_discount_updated: fieldsOnly("alterou"),

  subscription_non_renewal_requested: summarizeNonRenewalRequested,
  subscription_non_renewal_canceled: summarizeNonRenewalCanceled,
  subscription_billing_option_change_requested: summarizeBillingOptionChange,
  subscription_invitation_closed_dead_payment: summarizeInvitationClosedDeadPayment,
  subscription_invitation_closed_subscription_ended: summarizeInvitationClosedSubscriptionEnded,

  tier_created: tierWith(() => []),
  tier_updated: tierWith((d) => [fieldsFragment(d, "alterou")]),
  // Hard delete: the tier is always gone and the row records no name.
  tier_deleted: (row) =>
    buildSummary(
      isUuid(row.entity_id)
        ? { text: missingText("tier"), state: "missing", id: row.entity_id }
        : entityPrimary(row),
    ),
  tier_forms_published: tierWith((d) => [
    countFragment(d, "migrated", "assinatura migrada", "assinaturas migradas"),
    countFragment(
      d,
      "minted_form_prices",
      "preço criado na Stripe",
      "preços criados na Stripe",
    ),
  ]),
  tier_forms_mirrored: tierWith((d) => [
    countFragment(d, "minted", "forma criada", "formas criadas"),
  ]),
  tier_stripe_synced: summarizeTierStripeSynced,
  tier_billing_options_synced: tierWith((d) => [
    countFragment(d, "count", "forma", "formas"),
  ]),
  tier_billing_option_deactivated: summarizeTierBillingOptionDeactivated,

  billing_snapshot_updated: fieldsOnly("alterou"),
  dunning_config_updated: fieldsOnly("alterou"),
  dunning_step_updated: fieldsOnly("alterou"),
  renewal_reminder_rule_created: fieldsOnly("campos:"),
  renewal_reminder_rule_updated: fieldsOnly("alterou"),
  renewal_reminder_rule_deleted: (row) => buildSummary(entityPrimary(row)),

  "invoice.retry_succeeded": summarizeInvoiceRetry("ok"),
  "invoice.retry_failed": summarizeInvoiceRetry("failed"),
  "payment.declined": summarizePaymentDeclined,
  "activity_payment.previous_attempt_settled": summarizePreviousAttempt,

  "payment_method.attached": summarizeCardAttached,
  "payment_method.saved_from_purchase": summarizeCardSaved,
  "payment_method.detached": summarizeCardDetached,
  "payment_method.set_default": summarizeCardSetDefault,
  "payment_method.set_role": summarizeCardSetRole,
  "payment_method.detached_on_account_deletion":
    summarizeCardsDetachedOnDeletion,
  payments_retained: summarizePaymentsRetained,

  stripe_webhook_orphan_subscription: summarizeOrphanSubscription,
  tenant_stripe_connected: summarizeStripeConnected,
  tenant_stripe_connect_refused: summarizeStripeRefused,
};

const collectGrant: RefCollector = (row, sink) => {
  const d = asRecord(row.details);
  sink.person(d?.user_id);
  sink.target("tier", d?.tier_id);
};

const collectTier: RefCollector = (row, sink) =>
  sink.target("tier", row.entity_id);

const collectOwner: RefCollector = (row, sink) =>
  sink.person(asRecord(row.details)?.owner_user_id);

/** The person whose invitation closed; the closer writes `details.user_id`. */
const collectInvitationOwner: RefCollector = (row, sink) =>
  sink.person(asRecord(row.details)?.user_id);

export const refCollectors: Partial<Record<string, RefCollector>> = {
  membership_discount_granted: collectGrant,
  membership_discount_replaced: collectGrant,
  membership_discount_revoked: (row, sink) => {
    sink.person(asRecord(row.details)?.user_id);
    sink.target("subscription_discount_grant", row.entity_id);
  },

  subscription_non_renewal_requested: collectOwner,
  subscription_non_renewal_canceled: (row, sink) => {
    collectOwner(row, sink);
    sink.target(
      "tier_billing_option",
      asRecord(row.details)?.restored_billing_option_id,
    );
  },
  subscription_billing_option_change_requested: (row, sink) => {
    collectOwner(row, sink);
    sink.target(
      "tier_billing_option",
      asRecord(row.details)?.scheduled_billing_option_id,
    );
  },
  subscription_invitation_closed_dead_payment: collectInvitationOwner,
  subscription_invitation_closed_subscription_ended: collectInvitationOwner,

  tier_created: collectTier,
  tier_updated: collectTier,
  tier_forms_published: collectTier,
  tier_forms_mirrored: collectTier,
  tier_stripe_synced: collectTier,
  tier_billing_options_synced: collectTier,
  tier_billing_option_deactivated: (row, sink) => {
    sink.target("tier_billing_option", row.entity_id);
    sink.target("tier", asRecord(row.details)?.tier_id);
  },

  "payment.declined": (row, sink) => {
    const d = asRecord(row.details);
    sink.person(d?.user_id);
    if (str(d, "entity_type") === "activity")
      sink.target("activity", d?.entity_id);
  },
  "activity_payment.previous_attempt_settled": (row, sink) => {
    sink.target("activity", row.entity_id);
    sink.person(asRecord(row.details)?.host_user_id);
  },

  stripe_webhook_orphan_subscription: (row, sink) => {
    if (row.entity_type === "profile") sink.person(row.entity_id);
    sink.target("tier", asRecord(row.details)?.metadata_tier_id);
  },
};
